import { useQuery } from "@tanstack/react-query";
import { apiGet, ApiError } from "../api/client";

export type GovernancePeriod = "day" | "week" | "month" | "quarter";

export interface GovernanceMetricRow {
  id: string;
  metric_type: string;
  period: string;
  period_start: string;
  period_end: string;
  values: unknown;
  computed_at: string;
}

export interface GovernanceListResponse {
  rows: GovernanceMetricRow[];
}

export interface OutcomeMetric {
  id: string;
  kpi_name: string;
  projected_value: number | string | null;
  achieved_value: number | string | null;
  unit: string | null;
  capture_timestamp: string;
  source: string;
  source_ref: string | null;
}

export interface PlayOutcome {
  id: string;
  play_id: string;
  workspace_id: string;
  capture_mode: string;
  achieved_vs_projected: unknown;
  lessons_learned: string | null;
  recorded_at: string;
  finalized_at: string | null;
  metrics: OutcomeMetric[];
}

/**
 * Compute the period range string in YYYY-MM-DD form for a "last N days"
 * window ending today (UTC). Governance endpoints require both `from` and
 * `to` so we can't pass a relative range — derive concrete dates here.
 */
function rangeWindow(daysBack: number): { from: string; to: string } {
  const now = new Date();
  const today = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );
  const start = new Date(today.getTime() - daysBack * 24 * 60 * 60 * 1000);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { from: fmt(start), to: fmt(today) };
}

function buildQs(qs: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(qs)) {
    if (v !== undefined && v !== "") sp.set(k, String(v));
  }
  return sp.toString();
}

export function useGovernanceVelocity(
  workspaceId: string,
  period: GovernancePeriod = "week",
  daysBack = 90,
) {
  const range = rangeWindow(daysBack);
  const qs = buildQs({ period, from: range.from, to: range.to });
  return useQuery<GovernanceListResponse, Error>({
    queryKey: ["gov", "velocity", workspaceId, period, daysBack],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<GovernanceListResponse>(
        `/api/workspaces/${workspaceId}/governance/velocity?${qs}`,
      ),
    staleTime: 60_000,
    retry: false,
  });
}

export function useGovernanceAccuracy(
  workspaceId: string,
  period: GovernancePeriod = "week",
  daysBack = 90,
) {
  const range = rangeWindow(daysBack);
  const qs = buildQs({ period, from: range.from, to: range.to });
  return useQuery<GovernanceListResponse, Error>({
    queryKey: ["gov", "accuracy", workspaceId, period, daysBack],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<GovernanceListResponse>(
        `/api/workspaces/${workspaceId}/governance/accuracy?${qs}`,
      ),
    staleTime: 60_000,
    retry: false,
  });
}

export function useGovernanceReplanFrequency(
  workspaceId: string,
  period: GovernancePeriod = "week",
  daysBack = 90,
) {
  const range = rangeWindow(daysBack);
  const qs = buildQs({ period, from: range.from, to: range.to });
  return useQuery<GovernanceListResponse, Error>({
    queryKey: ["gov", "replan-frequency", workspaceId, period, daysBack],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<GovernanceListResponse>(
        `/api/workspaces/${workspaceId}/governance/replan-frequency?${qs}`,
      ),
    staleTime: 60_000,
    retry: false,
  });
}

export function useGovernanceDecisionLatency(
  workspaceId: string,
  period: GovernancePeriod = "week",
  daysBack = 90,
) {
  const range = rangeWindow(daysBack);
  const qs = buildQs({ period, from: range.from, to: range.to });
  return useQuery<GovernanceListResponse, Error>({
    queryKey: ["gov", "decision-latency", workspaceId, period, daysBack],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<GovernanceListResponse>(
        `/api/workspaces/${workspaceId}/governance/decision-latency?${qs}`,
      ),
    staleTime: 60_000,
    retry: false,
  });
}

export function useGovernancePeerBenchmarks(
  workspaceId: string,
  period: GovernancePeriod = "week",
  daysBack = 90,
  cohortMin = 10,
) {
  const range = rangeWindow(daysBack);
  const qs = buildQs({
    period,
    from: range.from,
    to: range.to,
    cohort_min: cohortMin,
  });
  return useQuery<GovernanceListResponse, Error>({
    queryKey: ["gov", "peer-benchmarks", workspaceId, period, daysBack, cohortMin],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<GovernanceListResponse>(
        `/api/workspaces/${workspaceId}/governance/peer-benchmarks?${qs}`,
      ),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * Fetch a play outcome. Returns null (not throws) when the API responds
 * 404 — common for in-flight or unfinalized plays.
 */
export function usePlayOutcome(workspaceId: string, playId: string) {
  return useQuery<PlayOutcome | null, Error>({
    queryKey: ["outcome", workspaceId, playId],
    enabled: !!workspaceId && !!playId,
    queryFn: async () => {
      try {
        return await apiGet<PlayOutcome>(
          `/api/workspaces/${workspaceId}/plays/${playId}/outcome`,
        );
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    retry: false,
  });
}
